import { useEffect, useState, useReducer } from 'react';
import { Auth as authConstants } from '../../constants';
import Auth from '@aws-amplify/auth';
import { ICredentials } from '@aws-amplify/core';
import Cognito from 'aws-sdk/clients/cognitoidentityserviceprovider';

type AWSError = import('aws-sdk').AWSError;

type CognitoState = {
  loading: boolean;
  error: null | Error | AWSError;
  client: null | Cognito;
};

type CognitoAction =
  | {
      type: 'client';
      client: Cognito;
    }
  | { type: 'error'; error: Error | AWSError }
  | { type: 'loading' };

function reducer(state: CognitoState, action: CognitoAction): CognitoState {
  switch (action.type) {
    case 'loading':
      return {
        ...state,
        loading: true,
      };
    case 'client':
      return {
        loading: false,
        error: null,
        client: action.client,
      };
    case 'error':
      return {
        loading: false,
        error: action.error,
        client: null,
      };
  }
}

async function getClient(credentials: ICredentials) {
  const cognito = new Cognito({
    region: authConstants.region,
    credentials: Auth.essentialCredentials(credentials),
  });

  return cognito;
}

export function useCognito() {
  const [credentials, setCredentials] = useState<null | ICredentials>(null);

  const [state, dispatch] = useReducer(reducer, {
    loading: true,
    error: null,
    client: null,
  });

  useEffect(() => {
    let isMounted = true;

    Auth.currentCredentials()
      .then(credentials => {
        if (!isMounted) {
          return;
        }

        if (credentials instanceof Error) {
          throw credentials;
        }

        setCredentials(credentials);
      })
      .catch(error => {
        if (!isMounted) {
          return;
        }

        dispatch({ type: 'error', error });
      });

    return () => {
      isMounted = false;
    };
  }, [setCredentials, dispatch]);

  useEffect(() => {
    let isMounted = true;
    const unmount = () => {
      isMounted = false;
    };

    if (!credentials) {
      return unmount;
    }

    getClient(credentials)
      .then(client => {
        if (!isMounted) {
          return;
        }

        dispatch({ type: 'client', client });
      })
      .catch(error => {
        if (!isMounted) {
          return;
        }

        dispatch({ type: 'error', error });
      });

    return unmount;
  }, [credentials, dispatch]);

  return state;
}
